import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  CoverageRequest, CoverageRequestType, Employee, Shift,
} from "../lib/types";
import { coverageRequestTypeLabel } from "../lib/coverage";
import { Modal } from "./ui/Modal";
import { Button } from "./ui/Button";

// Helper: minute-of-day → "HH:MM"
function fmtHM(min: number): string {
  const m = ((min % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}
function parseHM(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 24 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

// Given a shift and the coverage-type the requester picked, compute
// the default OT hours for that coverage. Full uses the whole shift;
// splits use the shift's midpoint.
function defaultHoursFor(
  shift: Shift,
  coverageType: CoverageRequestType,
): { start: string; end: string } {
  const start = parseHM(shift.startTime) ?? 0;
  const end = start + shift.durationMin;
  const midpoint = start + Math.round(shift.durationMin / 2);
  switch (coverageType) {
    case "full":
      return { start: fmtHM(start), end: fmtHM(end) };
    case "split_first":
      return { start: fmtHM(start), end: fmtHM(midpoint) };
    case "split_second":
      return { start: fmtHM(midpoint), end: fmtHM(end) };
  }
}

// The "Request" button on an understaffed row + the small modal it
// opens for coverage-type + optional note. Only rendered when the
// eligibility helper says the current officer has at least one type
// they can request. The button label is deliberately compact ("→
// Request") so it fits on the tight understaffed row layout.
export function RequestCoverageButton({
  shiftId, deptId, date, eligibleTypes,
}: {
  shiftId: string;
  deptId: string;
  date: string;
  eligibleTypes: CoverageRequestType[];
}) {
  const [open, setOpen] = useState(false);
  const [coverageType, setCoverageType] = useState<CoverageRequestType>(
    eligibleTypes[0] ?? "full",
  );
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const submit = useMutation({
    mutationFn: () => api.createCoverageRequest({
      shiftId, departmentId: deptId, date, coverageType,
      requesterNote: note,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["coverageRequests"] });
      setOpen(false);
      setNote("");
      setError(null);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Submit failed.";
      setError(msg);
    },
  });

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="text-[10px] font-semibold uppercase tracking-wider text-sky-700 hover:text-sky-900 hover:underline whitespace-nowrap"
        title="Request to cover this shift"
      >
        Request →
      </button>
      {open && (
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          title="Request to cover this shift"
          footer={
            <>
              <Button variant="secondary" onClick={() => setOpen(false)}
                disabled={submit.isPending}>
                Cancel
              </Button>
              <Button onClick={() => submit.mutate()}
                disabled={submit.isPending}>
                {submit.isPending ? "Submitting…" : "Submit request"}
              </Button>
            </>
          }
        >
          <div className="space-y-4 text-sm">
            <p className="text-slate-600">
              Your request will be sent to the department supervisor.
              You'll get a notification when they approve or deny it.
            </p>
            <div>
              <label className="block text-xs font-medium text-slate-700 uppercase tracking-wider mb-1">
                Coverage
              </label>
              {eligibleTypes.length === 1 ? (
                <p className="text-slate-900">
                  {coverageRequestTypeLabel(eligibleTypes[0])}
                </p>
              ) : (
                <select
                  className="w-full rounded border-slate-300 text-sm"
                  value={coverageType}
                  onChange={(e) => setCoverageType(e.target.value as CoverageRequestType)}
                >
                  {eligibleTypes.map((t) => (
                    <option key={t} value={t}>
                      {coverageRequestTypeLabel(t)}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 uppercase tracking-wider mb-1">
                Note (optional)
              </label>
              <textarea
                className="w-full rounded border-slate-300 text-sm"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Anything the supervisor should know?"
              />
            </div>
            {error && (
              <p className="text-sm text-rose-700">{error}</p>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

// Supervisor approve / deny modal for a single coverage request.
// Shows the requester + shift + date, an OT hours editor (pre-
// filled from the coverage-type default), a comment box, and the
// two decision buttons. Approve creates the OT assignment and
// links it back to the request row via decideCoverageRequest.
export function ReviewCoverageRequestModal({
  request, shift, requester, onClose,
}: {
  request: CoverageRequest;
  shift: Shift | null;
  requester: Employee | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const defaults = useMemo(
    () => shift ? defaultHoursFor(shift, request.coverageType) : { start: "", end: "" },
    [shift, request.coverageType],
  );
  const [start, setStart] = useState(defaults.start);
  const [end, setEnd] = useState(defaults.end);
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: async (decision: "approved" | "denied") => {
      if (decision === "denied") {
        return api.decideCoverageRequest({
          id: request.id, decision, comment,
        });
      }
      if (!shift) throw new Error("Shift not found.");
      if (!requester) throw new Error("Requester not found.");
      if (!parseHM(start) || !parseHM(end)) {
        throw new Error("OT hours must be HH:MM.");
      }
      const coverageGroupId = crypto.randomUUID();
      const overrideReason =
        request.coverageType === "full"
          ? "Overtime — full coverage (request approved)"
          : request.coverageType === "split_first"
          ? "Overtime — covering first half (request approved)"
          : "Overtime — covering second half (request approved)";
      const asn = await api.createAssignment({
        employeeId: requester.id,
        shiftId: shift.id,
        date: request.date,
        note: "OT",
        overrideReason,
        coverageGroupId,
        otStartTime: start,
        otEndTime: end,
      });
      return api.decideCoverageRequest({
        id: request.id, decision, comment,
        assignmentId: asn.id,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["coverageRequests"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
      qc.invalidateQueries({ queryKey: ["assignments"] });
      onClose();
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Decision failed.";
      setError(msg);
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Coverage request"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}
            disabled={decide.isPending}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={() => decide.mutate("denied")}
            disabled={decide.isPending}
          >
            Deny
          </Button>
          <Button
            onClick={() => decide.mutate("approved")}
            disabled={decide.isPending}
          >
            {decide.isPending ? "Working…" : "Approve"}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <dl className="grid grid-cols-3 gap-y-2 text-slate-700">
          <dt className="col-span-1 text-xs uppercase tracking-wider text-slate-500">Officer</dt>
          <dd className="col-span-2">
            {requester ? `${requester.lastName}, ${requester.firstName}` : "—"}
          </dd>
          <dt className="col-span-1 text-xs uppercase tracking-wider text-slate-500">Shift</dt>
          <dd className="col-span-2">{shift?.name ?? "—"}</dd>
          <dt className="col-span-1 text-xs uppercase tracking-wider text-slate-500">Date</dt>
          <dd className="col-span-2">{request.date}</dd>
          <dt className="col-span-1 text-xs uppercase tracking-wider text-slate-500">Requested</dt>
          <dd className="col-span-2">
            {coverageRequestTypeLabel(request.coverageType)}
          </dd>
          {request.requesterNote && (
            <>
              <dt className="col-span-1 text-xs uppercase tracking-wider text-slate-500">Note</dt>
              <dd className="col-span-2 whitespace-pre-wrap">
                {request.requesterNote}
              </dd>
            </>
          )}
        </dl>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 uppercase tracking-wider mb-1">
              OT start
            </label>
            <input
              type="time"
              className="w-full rounded border-slate-300 text-sm"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 uppercase tracking-wider mb-1">
              OT end
            </label>
            <input
              type="time"
              className="w-full rounded border-slate-300 text-sm"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 uppercase tracking-wider mb-1">
            Comment (optional)
          </label>
          <textarea
            className="w-full rounded border-slate-300 text-sm"
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Sent with the notification to the officer."
          />
        </div>

        {error && <p className="text-sm text-rose-700">{error}</p>}
      </div>
    </Modal>
  );
}
